import React, { FC, useMemo } from "react";
import styled from "styled-components";

import type {
  AOTDServerData,
  PipelineStep,
  RaceParticipantEntryData,
} from "./types";
import {
  Card,
  Grid,
  Pill,
  Row,
  SectionTitle,
  colors,
} from "./styles";

interface Props {
  data: AOTDServerData;
}

const Strip = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 16px;
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const Preview = styled(Card)`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Thumb = styled.div`
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${colors.border};
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  span {
    color: ${colors.textDim};
    font-size: 0.85rem;
  }
`;

const PreviewMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PreviewTitle = styled.div`
  font-size: 0.98rem;
  font-weight: 700;
  line-height: 1.3;
  color: ${colors.text};
`;

const PreviewSub = styled.div`
  font-size: 0.78rem;
  color: ${colors.textMuted};
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
`;

const Emojis = styled.div`
  font-size: 1.4rem;
  letter-spacing: 4px;
  line-height: 1.1;
`;

const StatCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const StatGrid = styled(Grid)`
  align-items: stretch;
`;

const StatCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const StatLabel = styled.div`
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.7px;
  color: ${colors.textMuted};
  font-weight: 600;
`;

const StatValue = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  color: ${colors.text};
`;

const StatHint = styled.div`
  font-size: 0.78rem;
  color: ${colors.textMuted};
`;

const STATE_ORDER = [
  "initialized",
  "generated_prompt",
  "generated_image",
  "posted_tweet",
  "posted_tg",
  "done",
] as const;

function stateReached(state: string | undefined, target: string): boolean {
  if (!state) return false;
  const cur = STATE_ORDER.indexOf(state as any);
  const tgt = STATE_ORDER.indexOf(target as any);
  return cur >= 0 && tgt >= 0 && cur >= tgt;
}

function fmtMs(ms?: number | null): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m}m ${s}s`;
}

function findSlowestStep(steps: PipelineStep[] | undefined): PipelineStep | null {
  if (!steps || steps.length === 0) return null;
  return steps.reduce((a, b) => (b.durationMs > a.durationMs ? b : a));
}

function findWinner(
  participants: RaceParticipantEntryData[] | undefined,
): RaceParticipantEntryData | null {
  if (!participants?.length) return null;
  return participants.find((p) => p.outcome === "winner") ?? null;
}

function fastestOffset(participants?: RaceParticipantEntryData[]): number | null {
  if (!participants?.length) return null;
  const sorted = [...participants]
    .filter((p) => p.effectiveSentAtMs != null)
    .sort((a, b) => (a.effectiveSentAtMs as number) - (b.effectiveSentAtMs as number));
  if (sorted.length < 2) return null;
  return (sorted[1].effectiveSentAtMs as number) - (sorted[0].effectiveSentAtMs as number);
}

export const SummaryStrip: FC<Props> = ({ data }) => {
  const gs = data.generationStatus;
  const wb = data.metadata?.wayback;
  const tweet = data.tweet;
  const pl = data.pipelineMetadata;
  const ds = data.dayState;

  const overallTone: "ok" | "warn" | "err" | "info" | "muted" = useMemo(() => {
    if (gs?.status === "error" || gs?.status === "failed") return "err";
    if (gs?.state === "done") return "ok";
    if (gs?.status === "generating" || gs?.status === "started") return "info";
    return "muted";
  }, [gs]);

  const imageDone = stateReached(gs?.state, "generated_image") || !!data.url;
  const tweetDone =
    stateReached(gs?.state, "posted_tweet") || !!tweet?.tweetUrl;
  const tgDone = stateReached(gs?.state, "posted_tg");
  const wbDone = wb?.status === "completed";
  const wbVerified = !!wb?.verified;
  const slowest = findSlowestStep(pl?.steps);
  const winner = findWinner(ds?.participants);
  const offset = fastestOffset(ds?.participants);
  const cardUsed = ds?.participants?.find(
    (p) => p.outcome === "winner" && p.appliedAdvantageCardSlug,
  );
  const lineageCount = ds?.stolenLineage?.length || 0;

  const dateStr = data.date
    ? new Date(data.date).toISOString().slice(0, 10)
    : ds?.dayKey ?? "";

  return (
    <Strip>
      <Preview>
        <Thumb>
          {data.url ? (
            <img src={data.url} alt={data.title || "AOTD"} loading="lazy" />
          ) : (
            <span>No image yet</span>
          )}
        </Thumb>
        <PreviewMeta>
          <Emojis>{(data.emojis || []).join(" ") || "—"}</Emojis>
          <PreviewTitle>{data.title || "(no title)"}</PreviewTitle>
          <PreviewSub>
            <span>👤 {data.createdBy || "—"}</span>
            <span>📅 {dateStr || "—"}</span>
          </PreviewSub>
          <PreviewSub>
            <span>🎨 {data.imageModel || "—"}</span>
            {data.utcOffset != null && <span>🕒 UTC+{data.utcOffset}</span>}
          </PreviewSub>
        </PreviewMeta>
      </Preview>

      <StatCards>
        <div>
          <SectionTitle>Status</SectionTitle>
          <Row>
            <Pill $tone={overallTone}>
              {gs?.state === "done"
                ? "✓ completed"
                : gs?.status === "error" || gs?.status === "failed"
                ? `✗ ${gs.status}`
                : gs?.status === "generating" || gs?.status === "started"
                ? "● generating"
                : gs?.status || "—"}
            </Pill>
            <Pill $tone={imageDone ? "ok" : "muted"}>
              {imageDone ? "✓" : "○"} image
            </Pill>
            <Pill $tone={tweetDone ? "ok" : "muted"}>
              {tweetDone ? "✓" : "○"} tweet
            </Pill>
            <Pill $tone={tgDone ? "ok" : "muted"}>
              {tgDone ? "✓" : "○"} telegram
            </Pill>
            <Pill
              $tone={
                wbDone && wbVerified
                  ? "ok"
                  : wbDone
                  ? "info"
                  : wb?.status === "failed"
                  ? "err"
                  : "muted"
              }
            >
              {wbDone ? (wbVerified ? "✓" : "?") : wb?.status === "failed" ? "✗" : "○"}{" "}
              wayback
            </Pill>
            {gs?.error && <Pill $tone="err" title={gs.error}>error</Pill>}
          </Row>
        </div>

        <StatGrid $minColPx={180}>
          <StatCard>
            <StatLabel>Pipeline</StatLabel>
            <StatValue>
              {pl?.totalDurationMs != null
                ? fmtMs(pl.totalDurationMs)
                : "—"}
            </StatValue>
            <StatHint>
              {pl?.steps?.length
                ? `${pl.steps.length} step${pl.steps.length === 1 ? "" : "s"}`
                : "no steps recorded"}
              {slowest
                ? ` · slowest: ${slowest.name} (${fmtMs(slowest.durationMs)})`
                : ""}
            </StatHint>
          </StatCard>

          <StatCard>
            <StatLabel>Race</StatLabel>
            <StatValue>{winner?.username || "—"}</StatValue>
            <StatHint>
              {ds?.participants?.length
                ? `${ds.participants.length} participant${
                    ds.participants.length === 1 ? "" : "s"
                  }`
                : "no race recorded"}
              {offset != null ? ` · margin ${fmtMs(offset)}` : ""}
              {cardUsed?.appliedAdvantageCardSlug
                ? ` · 🎴 ${cardUsed.appliedAdvantageCardSlug}`
                : ""}
              {lineageCount > 0 ? ` · ${lineageCount} steal${lineageCount === 1 ? "" : "s"}` : ""}
            </StatHint>
          </StatCard>

          <StatCard>
            <StatLabel>Views</StatLabel>
            <StatValue>{data.views?.total ?? 0}</StatValue>
            <StatHint>
              {data.views?.viewedBy?.length ?? 0} unique viewer
              {(data.views?.viewedBy?.length ?? 0) === 1 ? "" : "s"}
            </StatHint>
          </StatCard>

          <StatCard>
            <StatLabel>Day state</StatLabel>
            <StatValue>{ds?.state || "—"}</StatValue>
            <StatHint>
              {ds?.dayKey ? `key: ${ds.dayKey}` : "no dayState row"}
            </StatHint>
          </StatCard>
        </StatGrid>
      </StatCards>
    </Strip>
  );
};
